import { getAgencyServicePromiseClient, getTrainerServicePromiseClient } from '../utils/grpcClient';
import {
    CreateTrainerAccountRequest,
    GetAgencyAllTrainersRequest,
    GetAgencySingleTrainerRequest,
    TrainerAccountResponse,
    GetAgencyAllTrainersResponse,
    GetAgencySingleTrainerResponse,
    ActivateTrainerAccountRequest,
    DeactivateTrainerAccountRequest,
} from '../generated/fitness-service/v1/agency_pb';

import {
    GetMyClientsRequest,
    GetMyClientsResponse,
    GetMyProfileRequest,
    GetMyProfileResponse,
} from '../generated/fitness-service/v1/trainer_pb';
import { TrainerProfile, TrainerClientRelation } from '../generated/fitness-service/v1/common_pb';

export interface CreateTrainerData {
    name: string;
    email: string;
    password: string;
    phone: string;
    specialization: string;
    certification: string;
    experienceYears: number;
}

export interface GetTrainersParams {
    page?: number;
    limit?: number;
    activeOnly?: boolean;
}

export interface GetTrainerClientsParams {
    page?: number;
    limit?: number;
    active_only?: boolean;
}

export interface TrainerClient {
    id: string;
    name: string;
    email: string;
    phone?: string;
    date_of_birth?: string;
    gender?: string;
    is_active: boolean;
    created_at: string;
    trainer_count: number;
    join_date: string;
}

export interface TrainerClientsResponse {
    clients: TrainerClient[];
    total_count: number;
    page: number;
    limit: number;
}

export class TrainerService {
    private agencyClient = getAgencyServicePromiseClient();
    private trainerClient = getTrainerServicePromiseClient();

    private getAuthMetadata() {
        const token = localStorage.getItem('authToken'); // Changed from 'accessToken' to 'authToken'
        if (!token) {
            throw new Error('No access token found');
        }
        return { authorization: `Bearer ${token}` };
    }

    async createTrainer(data: CreateTrainerData): Promise<TrainerAccountResponse.AsObject> {
        const request = new CreateTrainerAccountRequest();
        request.setName(data.name);
        request.setEmail(data.email);
        request.setPassword(data.password);
        request.setPhone(data.phone);
        request.setSpecialization(data.specialization);
        request.setCertification(data.certification);
        request.setExperienceYears(data.experienceYears);

        const response = await this.agencyClient.createTrainerAccount(request, this.getAuthMetadata());
        return response.toObject();
    }

    async getTrainers(params: GetTrainersParams = {}): Promise<GetAgencyAllTrainersResponse.AsObject> {
        const request = new GetAgencyAllTrainersRequest();
        request.setPage(params.page || 1);
        request.setLimit(params.limit || 10);
        request.setActiveOnly(params.activeOnly || false);

        const response = await this.agencyClient.getAgencyAllTrainers(request, this.getAuthMetadata());
        return response.toObject();
    }

    async getTrainer(trainerId: string): Promise<GetAgencySingleTrainerResponse.AsObject> {
        const request = new GetAgencySingleTrainerRequest();
        request.setTrainerId(trainerId);

        const response = await this.agencyClient.getAgencySingleTrainer(request, this.getAuthMetadata());
        return response.toObject();
    }

    async activateTrainer(trainerId: string): Promise<TrainerAccountResponse.AsObject> {
        const request = new ActivateTrainerAccountRequest();
        request.setTrainerId(trainerId);

        const response = await this.agencyClient.activateTrainerAccount(request, this.getAuthMetadata());
        return response.toObject();
    }

    async deactivateTrainer(trainerId: string): Promise<TrainerAccountResponse.AsObject> {
        const request = new DeactivateTrainerAccountRequest();
        request.setTrainerId(trainerId);

        const response = await this.agencyClient.deactivateTrainerAccount(request, this.getAuthMetadata());
        return response.toObject();
    }

    async getTrainerClients(params: GetTrainerClientsParams = {}): Promise<TrainerClientsResponse> {
        console.log('TrainerService.getTrainerClients called with params:', params);
        const request = new GetMyClientsRequest();

        if (params.page !== undefined) request.setPage(params.page);
        if (params.limit !== undefined) request.setLimit(params.limit);
        if (params.active_only !== undefined) request.setActiveOnly(params.active_only);

        console.log('Sending gRPC request to TrainerService.GetMyClients:', {
            page: params.page,
            limit: params.limit,
            active_only: params.active_only
        });

        try {
            const response = await this.trainerClient.getMyClients(request, this.getAuthMetadata());
            console.log('Raw gRPC response:', response.toObject());

            const clients: TrainerClient[] = response.getClientRelationsList().map((relation: any) => {
                const client = relation.getClient();
                return {
                    id: client.getId() || '',
                    name: client.getName() || '',
                    email: client.getEmail() || '',
                    phone: client.getPhone() || '',
                    date_of_birth: client.getDateOfBirth() || '',
                    gender: client.getGender() || '',
                    is_active: client.getIsActive() || false,
                    created_at: client.getCreatedAt() || '',
                    trainer_count: client.getTrainerCount() || 0,
                    join_date: client.getJoinDate() || '',
                };
            });

            console.log('Processed clients:', clients);

            return {
                clients,
                total_count: response.getTotalCount(),
                page: response.getPage(),
                limit: response.getLimit(),
            };
        } catch (error) {
            console.error('Error fetching trainer clients:', error);
            throw error;
        }
    }

    async getMyProfile(): Promise<GetMyProfileResponse.AsObject> {
        console.log('TrainerService.getMyProfile called');
        const request = new GetMyProfileRequest();

        try {
            const response = await this.trainerClient.getMyProfile(request, this.getAuthMetadata());
            console.log('Raw trainer profile response:', response.toObject());
            return response.toObject();
        } catch (error) {
            console.error('Error fetching trainer profile:', error);
            throw error;
        }
    }
}

export const trainerService = new TrainerService();
